"use client";

import { Button, Column, Feedback, Heading, Input, PasswordInput, SmartLink, Text } from "@once-ui-system/core";
import { useState } from "react";
import { AuthFrame, FieldLabel, type PlatformProvider, SocialButtons } from "@/components/auth/AuthFrame";

type Mode = "password" | "sso";

export function LoginForm({
  providers,
  next,
  initialError,
  initialMode = "password",
}: {
  providers: PlatformProvider[];
  next: string;
  initialError: string | null;
  initialMode?: Mode;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [error, setError] = useState<string | null>(initialError);
  const [busy, setBusy] = useState(false);

  const post = (url: string, body: unknown) =>
    fetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

  // Values come from the form, not React state: browser autofill fills fields without change events.
  const submitPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const identifier = String(form.get("identifier") ?? "").trim();
    const password = String(form.get("password") ?? "");
    if (!identifier || !password) return setError("Enter your email or username and your password.");
    setBusy(true);
    setError(null);
    try {
      const res = await post("/api/auth/login", { identifier, password });
      if (res.ok) return window.location.assign(next);
      const data = await res.json().catch(() => null);
      if (data?.error?.code === "sso_required") setMode("sso");
      setError(data?.error?.message ?? "Sign-in failed. Please try again.");
    } catch {
      setError("The sign-in service is unreachable. Please try again shortly.");
    }
    setBusy(false);
  };

  const submitSso = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get("email") ?? "").trim();
    if (!email.includes("@")) return setError("Enter your work email.");
    setBusy(true);
    setError(null);
    try {
      const res = await post("/api/auth/sso/discover", { email, next });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.sso && data.url) return window.location.assign(data.url);
      setError(
        res.ok
          ? "Single sign-on isn't set up for this email. Sign in with your password, or ask your administrator."
          : (data?.error?.message ?? "Could not look up single sign-on."),
      );
    } catch {
      setError("The sign-in service is unreachable. Please try again shortly.");
    }
    setBusy(false);
  };

  return (
    <AuthFrame
      topAction={
        <Button variant="tertiary" size="s" href="/signup">
          Create workspace
        </Button>
      }
    >
      <Column gap="8" horizontal="center">
        <Heading as="h1" variant="heading-strong-xl" align="center">
          {mode === "sso" ? "Sign in with SSO" : "Welcome back"}
        </Heading>
        <Text variant="body-default-s" onBackground="neutral-weak" align="center">
          {mode === "sso"
            ? "We'll send you to your organization's identity provider."
            : "Sign in to your workspace to continue."}
        </Text>
      </Column>

      {error && <Feedback variant="danger" description={error} />}

      {mode === "password" ? (
        <>
          <SocialButtons providers={providers} intent="login" next={next} />
          <form onSubmit={submitPassword} method="post" action="/api/auth/login" noValidate>
            <input type="hidden" name="next" value={next} />
            <Column gap="16">
              <Column gap="8">
                <FieldLabel htmlFor="identifier">Email or username</FieldLabel>
                <Input
                  id="identifier"
                  name="identifier"
                  size="s"
                  placeholder="name@company.com"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </Column>
              <Column gap="8">
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <PasswordInput
                  id="password"
                  name="password"
                  size="s"
                  placeholder="Your password"
                  autoComplete="current-password"
                />
              </Column>
              <Button type="submit" fillWidth loading={busy}>
                Sign in
              </Button>
            </Column>
          </form>
          <Button
            variant="secondary"
            fillWidth
            prefixIcon="lock"
            onClick={() => {
              setError(null);
              setMode("sso");
            }}
          >
            Sign in with SSO
          </Button>
        </>
      ) : (
        <>
          <form onSubmit={submitSso} noValidate>
            <Column gap="16">
              <Column gap="8">
                <FieldLabel htmlFor="sso-email">Work email</FieldLabel>
                <Input
                  id="sso-email"
                  name="email"
                  type="email"
                  size="s"
                  placeholder="name@company.com"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </Column>
              <Button type="submit" fillWidth loading={busy} suffixIcon="arrowRight">
                Continue with SSO
              </Button>
            </Column>
          </form>
          <Button
            variant="tertiary"
            fillWidth
            onClick={() => {
              setError(null);
              setMode("password");
            }}
          >
            Use email and password instead
          </Button>
        </>
      )}

      <Text variant="body-default-s" onBackground="neutral-weak" align="center">
        New to the platform? <SmartLink href="/signup">Create a workspace</SmartLink>
      </Text>
    </AuthFrame>
  );
}
